import {
    Checkbox,
    Toggle,
    Label,
    ButtonGroup,
    ButtonGroupItem,
} from '@hm-group/bads-react';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import type { ConfirmationModalConfig } from '../ConfirmationModal/ConfirmationModal';
import type { Search } from '../../models';
import { LS, lsGetLabel } from '../../constants/constants';
import { MarketRegionSelect } from './MarketRegionSelect';
import styles from './SelectionBar.module.css';

export interface MarketListItem {
    value: string;
    label: string;
    visible: boolean;//Eye icon state – whether the market column is visible in the ag-grid
    unlinked: boolean;//Link icon state – true when the market has its own independent data (unlinked from main market)
    isMainMarket: boolean;
}

export interface MarketRegionConfig {
    channelLabel: string;
    name: string;
    testId: string;
    disabled: boolean;
    mainMarketName: string;
    markets: MarketListItem[];
    onEyeClick: (market: MarketListItem) => void;
    onLinkClick: (market: MarketListItem) => void;
}

export interface SelectionBarState {
    // Checkbox states
    isCoreExtendedChecked: boolean;
    isCoreExtendedDisabled: boolean;
    isExcitementChecked: boolean;
    isExcitementDisabled: boolean;

    // Toggle states
    isIncludeExcitementToggleChecked: boolean;
    isShowGroundArticlesToggleChecked: boolean;

    // Market regions (store + online)
    marketRegions: MarketRegionConfig[];

    // Button group
    selectedView: 'department' | 'section';
}

interface SelectionBarProps {
    isDepartmentSelected: boolean;
    isSectionSelected: boolean;
    state: SelectionBarState;
    searchBarData: Search;
    scaModalConfig: ConfirmationModalConfig;
    marketModalConfig: ConfirmationModalConfig;
    onCoreExtendedChange: (checked: boolean) => void;
    onExcitementChange: (checked: boolean) => void;
    onIncludeExcitementToggleChange: (checked: boolean) => void;
    onShowGroundArticlesToggleChange: (checked: boolean) => void;
    onViewChange: (view: 'department' | 'section') => void;
}

export function SelectionBar({
    isDepartmentSelected,
    isSectionSelected,
    state,
    searchBarData,
    scaModalConfig,
    marketModalConfig,
    onCoreExtendedChange,
    onExcitementChange,
    onIncludeExcitementToggleChange,
    onShowGroundArticlesToggleChange,
    onViewChange,
}: Readonly<SelectionBarProps>) {

    const showSelectionBar = isDepartmentSelected || isSectionSelected;

    if (!showSelectionBar) {
        return null;
    }

    const handleCoreExtendedClick = ({ checked }: { checked: boolean }) => {
        onCoreExtendedChange(checked);
    };

    const handleExcitementClick = ({ checked }: { checked: boolean }) => {
        onExcitementChange(checked);
    };

    const handleIncludeExcitementToggle = ({ checked }: { checked: boolean }) => {
        onIncludeExcitementToggleChange(checked);
    };

    const handleShowGroundArticlesToggle = ({ checked }: { checked: boolean }) => {
        onShowGroundArticlesToggleChange(checked);
    };

    const handleViewChange = (value: string) => {
        const view = value === 'section' ? 'section' : 'department';
        onViewChange(view);
    };

    // Build selection info display using labels from localStorage
    const selectionFields: Array<{ key: string; displayName: string; lsKey: string; hasValue: boolean }> = [
        { key: 'brand', displayName: 'Brand', lsKey: LS.brand, hasValue: !!searchBarData.brand },
        { key: 'customerGroup', displayName: 'Customer group', lsKey: LS.customerGroup, hasValue: !!searchBarData.customerGroup },
        { key: 'division', displayName: 'Division', lsKey: LS.division, hasValue: !!searchBarData.division },
        { key: 'section', displayName: 'Section', lsKey: LS.section, hasValue: !!searchBarData.section },
        { key: 'department', displayName: 'Department', lsKey: LS.department, hasValue: (searchBarData.departments?.length ?? 0) > 0 },
    ];

    const selectionParts = selectionFields
        .filter((f) => f.hasValue)
        .map((f) => ({ key: f.key, label: lsGetLabel(f.lsKey), displayName: f.displayName }))
        .filter((f): f is typeof f & { label: string } => !!f.label)
        .map((f) => ({
            key: f.key,
            element: (
                <span>
                    <Label labelSize="bold">{f.displayName}:</Label> {f.label}
                </span>
            ),
        }));

    return (
        <>
            {selectionParts.length > 0 && (
                <div className={styles.selectionInfoBanner}>
                    <Label labelSize="bold">Selected:</Label>{' '}
                    {selectionParts.map((item, index) => (
                        <span key={item.key}>
                            {index > 0 && ' > '}
                            {item.element}
                        </span>
                    ))}
                </div>
            )}
            <div className={styles.headerContainer}>
                {/* Core/Extended and Excitement Checkboxes */}
                <div className={styles.checkboxArea}>
                    <div className={styles.checkboxWrapper}>
                        <Checkbox
                            name="coreExtended"
                            label="Core/Extended"
                            checked={state.isCoreExtendedChecked}
                            disabled={state.isCoreExtendedDisabled}
                            onClick={handleCoreExtendedClick}
                        />
                    </div>
                    <div className={styles.checkboxWrapper}>
                        <Checkbox
                            name="excitement"
                            label="Excitement"
                            checked={state.isExcitementChecked}
                            disabled={state.isExcitementDisabled}
                            onClick={handleExcitementClick}
                        />
                    </div>
                </div>

                <div className={styles.excitementToggleContainer}>
                    <div className={styles.toggleRowLabel}>
                        <div style={{ color: state.isExcitementChecked ? 'var(--bads-color-primary-100)' : 'var(--bads-color-secondary-120)' }}>
                            <Label labelSize="bold">
                                Include excitement in Product Plan/sum
                            </Label>
                        </div>
                    </div>
                    <div className={styles.toggleRow}>
                        <Toggle
                            name="includeExcitementToggle"
                            checked={state.isIncludeExcitementToggleChecked}
                            disabled={state.isExcitementDisabled || !state.isExcitementChecked}
                            onChange={handleIncludeExcitementToggle}
                        />
                    </div>
                </div>

                {/* Market Select Shells */}
                <div className={styles.selectShellContainer}>
                    {state.marketRegions.map((region) => (
                        <div key={region.name} className={styles.selectShellWrapper}>
                            <MarketRegionSelect {...region} />
                        </div>
                    ))}
                </div>

                <div className={styles.toggleContainer}>
                    <div className={styles.toggleRowLabel}>
                        <Label labelSize="bold">
                            Show ground articles per week
                        </Label>
                    </div>
                    <div className={styles.toggleRow}>
                        <Toggle
                            name="showGroundArticlesToggle"
                            checked={state.isShowGroundArticlesToggleChecked}
                            disabled={false}
                            onChange={handleShowGroundArticlesToggle}
                        />
                    </div>
                </div>

                <div className={styles.buttonGroupContainer}>
                    <ButtonGroup
                        value={state.selectedView}
                        onChange={handleViewChange}
                    >
                        <ButtonGroupItem value="department" label="Department" initialSelected />
                        <ButtonGroupItem value="section" label="Section Summary" />
                    </ButtonGroup>
                </div>

                {/* SCA Confirmation Modal */}
                <ConfirmationModal config={scaModalConfig} />

                {/* Market Removal Confirmation Modal */}
                <ConfirmationModal config={marketModalConfig} />
            </div>
        </>
    );
}
